import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, GraduationCap, BriefcaseBusiness, ShieldCheck, LogIn } from 'lucide-react';
import { Button } from '../ui/button';
import { trackNavigationClick } from '@/lib/analytics';

// Canonical Resources navigation config — single source of truth for BOTH
// the desktop Resources dropdown and the mobile Resources collapsible.
// Add a new resource here once; both menus pick it up automatically.
const RESOURCE_LINKS = [
  { to: '/resources',                  label: 'All Resources',   description: 'Articles, study guides & more',                                    testId: null,                       mobileTestId: null },
  { to: '/resources/selective-entry',  label: 'Selective Entry', description: 'Maths · English · Mock exams · Downloads',                         testId: 'nav-selective-entry',      mobileTestId: 'nav-selective-entry-mobile' },
  { to: '/resources/digital-sat',      label: 'Digital SAT',     description: 'Reading & Writing · Math · Full mocks',                            testId: 'nav-digital-sat',          mobileTestId: 'nav-digital-sat-mobile' },
  { to: '/resources/pte-academic',     label: 'PTE Academic',    description: 'Speaking · Writing · Reading · Listening · Daily Challenge',      testId: 'nav-pte-academic',         mobileTestId: 'nav-pte-academic-mobile' },
  { to: '/gmat',                       label: 'GMAT Prep',       description: 'Adaptive practice + full mocks',                                    testId: 'nav-gmat-prep',            mobileTestId: 'nav-gmat-prep-mobile' },
  { to: '/ielts',                      label: 'IELTS Prep',      description: 'Academic + General · 4 sections',                                   testId: 'nav-ielts-prep',           mobileTestId: 'nav-ielts-prep-mobile' },
  { to: '/blog',                       label: 'Knowledge Hub',   description: 'Free articles, guides & study tips',                                testId: 'nav-resources-blog-link',  mobileTestId: 'mobile-nav-resources-blog-link' },
];

const Navigation = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const [mobilePortalOpen, setMobilePortalOpen] = useState(false);
  
  // Helper function to check if link is active
  const isActive = (path) => location.pathname === path;
  const isResourcesActive = location.pathname === '/resources' || location.pathname.startsWith('/gmat') || location.pathname.startsWith('/ielts') || location.pathname.startsWith('/resources/selective') || location.pathname.startsWith('/selective') || location.pathname.startsWith('/resources/digital-sat') || location.pathname.startsWith('/dsat') || location.pathname.startsWith('/resources/pte-academic') || location.pathname.startsWith('/pte') || location.pathname.startsWith('/blog');
  
  // Scroll to section on homepage or navigate to home then scroll
  const scrollToSection = (sectionId) => {
    if (location.pathname === '/') {
      // Already on homepage, just scroll
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to home, then scroll after navigation
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
    setMobileMenuOpen(false);
  };
  
  return (
    <>
      <nav className="nav-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Clickable Logo */}
          <Link to="/" className="flex items-center cursor-pointer hover:opacity-90 transition-opacity">
            <img
              src="/winhouse-logo.png"
              alt="Winhouse Tutors"
              className="logo-image"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/tutors" 
              onClick={() => trackNavigationClick('Find a Tutor', '/tutors')}
              className={`transition-colors font-medium ${
                isActive('/tutors') 
                  ? 'text-[var(--primary-black)] font-semibold' 
                  : 'text-[var(--text-grey)] hover:text-[var(--primary-black)]'
              }`}
            >
              Find a Tutor
            </Link>
            {/* Resources dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setResourcesOpen(true)}
              onMouseLeave={() => setResourcesOpen(false)}
            >
              <button
                onClick={() => { trackNavigationClick('Resources', '/resources'); navigate('/resources'); }}
                className={`flex items-center gap-1 transition-colors font-medium ${
                  isResourcesActive
                    ? 'text-[var(--primary-black)] font-semibold'
                    : 'text-[var(--text-grey)] hover:text-[var(--primary-black)]'
                }`}
                data-testid="nav-resources"
              >
                Resources
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {resourcesOpen && (
                <div className="absolute left-0 top-full pt-2 w-56 z-40">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden py-1">
                    {RESOURCE_LINKS.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className="block px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                        onClick={() => setResourcesOpen(false)}
                        {...(link.testId ? { 'data-testid': link.testId } : {})}
                      >
                        <div className="font-medium text-[var(--primary-black)]">{link.label}</div>
                        <div className="text-xs text-gray-500">{link.description}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => scrollToSection('services')}
              className="text-[var(--text-grey)] hover:text-[var(--primary-black)] transition-colors font-medium cursor-pointer"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="text-[var(--text-grey)] hover:text-[var(--primary-black)] transition-colors font-medium cursor-pointer"
            >
              Pricing
            </button>
            <Link 
              to="/become-tutor" 
              onClick={() => trackNavigationClick('Become a Tutor', '/become-tutor')}
              className={`transition-colors font-medium ${
                isActive('/become-tutor') 
                  ? 'text-[var(--primary-black)] font-semibold' 
                  : 'text-[var(--text-grey)] hover:text-[var(--primary-black)]'
              }`}
            >
              Become a Tutor
            </Link>
          </div>
          
          <div className="flex items-center gap-3 md:gap-4">
            {/* Portal Login dropdown — desktop */}
            <div
              className="hidden md:block relative"
              onMouseEnter={() => setPortalOpen(true)}
              onMouseLeave={() => setPortalOpen(false)}
            >
              <button
                onClick={() => navigate('/portal/login')}
                className="flex items-center gap-1 text-[var(--text-grey)] hover:text-[var(--primary-black)] transition-colors font-medium"
                data-testid="nav-portal"
              >
                <LogIn className="h-3.5 w-3.5" />
                Portal Login
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${portalOpen ? 'rotate-180' : ''}`} />
              </button>
              {portalOpen && (
                <div className="absolute right-0 top-full pt-2 w-64 z-40">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden py-1">
                    <Link
                      to="/student/login"
                      className="flex items-start gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                      onClick={() => setPortalOpen(false)}
                      data-testid="nav-portal-student"
                    >
                      <GraduationCap className="h-4 w-4 text-red-700 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-[var(--primary-black)]">Student Login</div>
                        <div className="text-xs text-gray-500">View your lessons, recaps & homework</div>
                      </div>
                    </Link>
                    <Link
                      to="/tutor/login"
                      className="flex items-start gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                      onClick={() => setPortalOpen(false)}
                      data-testid="nav-portal-tutor"
                    >
                      <BriefcaseBusiness className="h-4 w-4 text-red-700 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-[var(--primary-black)]">Tutor Login</div>
                        <div className="text-xs text-gray-500">Your classes, students & lesson notes</div>
                      </div>
                    </Link>
                    <div className="my-1 border-t border-gray-100" />
                    <Link
                      to="/portal/login"
                      className="flex items-start gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                      onClick={() => setPortalOpen(false)}
                      data-testid="nav-portal-staff"
                    >
                      <ShieldCheck className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-[var(--primary-black)]">Winhouse Staff</div>
                        <div className="text-xs text-gray-500">Staff Hub · admin · owner</div>
                      </div>
                    </Link>
                  </div>
                </div>
              )}
            </div>
            {/* Desktop Get Started Button */}
            <div className="hidden md:block">
              <Button onClick={() => navigate('/get-started')} className="btn-primary">
                Get Started
              </Button>
            </div>
            {/* Mobile Get Started Button - Smaller */}
            <div className="block md:hidden">
              <Button onClick={() => navigate('/get-started')} className="btn-primary text-sm px-4 py-2">
                Get Started
              </Button>
            </div>
            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-3">
              <Link 
                to="/tutors" 
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 transition-colors font-medium ${
                  isActive('/tutors')
                    ? 'text-[var(--primary-black)] font-semibold'
                    : 'text-[var(--text-grey)] hover:text-[var(--primary-black)]'
                }`}
              >
                Find a Tutor
              </Link>
              {/* Resources collapsible group */}
              <div>
                <button
                  onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                  className={`flex items-center justify-between w-full py-2 transition-colors font-medium ${
                    isResourcesActive
                      ? 'text-[var(--primary-black)] font-semibold'
                      : 'text-[var(--text-grey)] hover:text-[var(--primary-black)]'
                  }`}
                  data-testid="nav-resources-mobile"
                >
                  Resources
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileResourcesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileResourcesOpen && (
                  <div className="ml-3 mt-1 mb-1 border-l border-gray-200 pl-4 space-y-2">
                    {RESOURCE_LINKS.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-1.5 text-sm text-[var(--text-grey)] hover:text-[var(--primary-black)]"
                        {...(link.mobileTestId ? { 'data-testid': link.mobileTestId } : {})}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => scrollToSection('services')}
                className="block w-full text-left py-2 text-[var(--text-grey)] hover:text-[var(--primary-black)] transition-colors font-medium"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="block w-full text-left py-2 text-[var(--text-grey)] hover:text-[var(--primary-black)] transition-colors font-medium"
              >
                Pricing
              </button>
              <Link 
                to="/become-tutor" 
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 transition-colors font-medium ${
                  isActive('/become-tutor')
                    ? 'text-[var(--primary-black)] font-semibold'
                    : 'text-[var(--text-grey)] hover:text-[var(--primary-black)]'
                }`}
              >
                Become a Tutor
              </Link>
              {/* Portal Login collapsible group (mobile) */}
              <div>
                <button
                  onClick={() => setMobilePortalOpen(!mobilePortalOpen)}
                  className="flex items-center justify-between w-full py-2 transition-colors font-medium text-[var(--text-grey)] hover:text-[var(--primary-black)]"
                  data-testid="nav-portal-mobile"
                >
                  <span className="flex items-center gap-1.5"><LogIn className="h-3.5 w-3.5" /> Portal Login</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobilePortalOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobilePortalOpen && (
                  <div className="ml-3 mt-1 mb-1 border-l border-gray-200 pl-4 space-y-2">
                    <Link
                      to="/student/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 py-1.5 text-sm text-[var(--text-grey)] hover:text-[var(--primary-black)]"
                      data-testid="nav-portal-student-mobile"
                    >
                      <GraduationCap className="h-3.5 w-3.5 text-red-700" /> Student Login
                    </Link>
                    <Link
                      to="/tutor/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 py-1.5 text-sm text-[var(--text-grey)] hover:text-[var(--primary-black)]"
                      data-testid="nav-portal-tutor-mobile"
                    >
                      <BriefcaseBusiness className="h-3.5 w-3.5 text-red-700" /> Tutor Login
                    </Link>
                    <Link
                      to="/portal/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 py-1.5 text-sm text-[var(--text-grey)] hover:text-[var(--primary-black)]"
                      data-testid="nav-portal-staff-mobile"
                    >
                      <ShieldCheck className="h-3.5 w-3.5 text-gray-500" /> Winhouse Staff
                    </Link>
                  </div>
                )}
              </div>
              <Button 
                onClick={() => {
                  navigate('/get-started');
                  setMobileMenuOpen(false);
                }} 
                className="btn-primary w-full text-sm py-3"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navigation;
